import { useCallback, useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { AlertCircle, Check, Heart, Loader2 } from 'lucide-react'
import {
  acceptAdvisorySuggestion,
  declineAdvisorySuggestion,
  getAdvisorySuggestions,
  type AdvisorySuggestion,
} from '../lib/api'

// "Care, not spam" — the idle-advisory surface on the calm home
// (PRODUCT_VISION: idle advisory). When the team notices a REAL fact about the
// client's world, it offers a gentle, optional next step tied to it. This card
// surfaces the OPEN offers and lets the client accept or decline — both final.
//
// Honesty rules that shape this component:
// - The list endpoint returns ONLY open suggestions. We never fabricate a
//   history, and an empty list renders NOTHING (no "no suggestions" spam block).
// - suggestion_text is the team's OWN voice — rendered verbatim, never
//   paraphrased, and never dressed in invented urgency chrome ("!", "act now").
// - Accept does NOT arm a response; it records the client's yes. Decline keeps
//   the offer and suppresses its kind server-side for 30 days. On either, the
//   card resolves in place — but only after the API confirms. A failed call
//   surfaces an honest error and leaves the offer exactly where it was; we never
//   fake success.

// A suggestion resolved during THIS session, kept so the card can transition to
// an acknowledged state in place rather than vanishing silently.
interface ResolvedEntry {
  suggestion: AdvisorySuggestion
  action: 'accepted' | 'declined'
}

interface SuggestionCardProps {
  suggestion: AdvisorySuggestion
  onResolved: (action: 'accepted' | 'declined') => void
}

function SuggestionCard({ suggestion, onResolved }: SuggestionCardProps) {
  const [busy, setBusy] = useState<'accept' | 'decline' | null>(null)
  const [error, setError] = useState<string | null>(null)

  const accept = useCallback(async () => {
    if (busy) return
    setBusy('accept')
    setError(null)
    try {
      await acceptAdvisorySuggestion(suggestion.id)
      onResolved('accepted')
    } catch {
      // Honest failure — the offer stays put, never a faked success.
      setError('That didn’t go through. Please try again.')
      setBusy(null)
    }
  }, [busy, suggestion.id, onResolved])

  const decline = useCallback(async () => {
    if (busy) return
    setBusy('decline')
    setError(null)
    try {
      await declineAdvisorySuggestion(suggestion.id)
      onResolved('declined')
    } catch {
      setError('That didn’t go through. Please try again.')
      setBusy(null)
    }
  }, [busy, suggestion.id, onResolved])

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.97 }}
      transition={{ duration: 0.25 }}
      className="rounded-2xl border border-gray-200 bg-white p-4 mb-3 shadow-sm"
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl bg-brass/10 flex items-center justify-center flex-shrink-0">
          <Heart className="w-4 h-4 text-brass" aria-hidden="true" />
        </div>
        <div className="flex-1 min-w-0">
          {suggestion.subject && (
            <p className="text-xs font-medium text-brass mb-0.5">
              About {suggestion.subject}
            </p>
          )}
          {/* The team's OWN words — rendered verbatim, never paraphrased. */}
          <p className="text-[15px] leading-snug text-navy whitespace-pre-wrap break-words">
            {suggestion.suggestion_text}
          </p>
        </div>
      </div>

      {error && (
        <p className="mt-2 text-xs text-danger" role="alert">
          {error}
        </p>
      )}

      <div className="mt-3 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={decline}
          disabled={busy !== null}
          className="tap-min inline-flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-50 transition-colors"
        >
          {busy === 'decline' ? (
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
          ) : null}
          No thanks
        </button>
        <button
          type="button"
          onClick={accept}
          disabled={busy !== null}
          className="tap-min inline-flex items-center gap-1.5 rounded-lg bg-brass px-3.5 py-2 text-sm font-medium text-white hover:bg-brass-dark disabled:opacity-40 transition-colors"
        >
          {busy === 'accept' ? (
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
          ) : (
            <Check className="w-4 h-4" aria-hidden="true" />
          )}
          Yes, please
        </button>
      </div>
    </motion.div>
  )
}

export function IdleAdvisoryCard({ principalId }: { principalId: string }) {
  const [suggestions, setSuggestions] = useState<AdvisorySuggestion[]>([])
  const [resolved, setResolved] = useState<ResolvedEntry[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      const data = await getAdvisorySuggestions(principalId)
      // The endpoint returns only OPEN suggestions; keep that contract explicit
      // so a status the server later reuses can never leak a resolved offer in.
      setSuggestions(data.filter((s) => s.status === 'open'))
    } catch {
      setError('We couldn’t load your team’s suggestions right now.')
    }
  }, [principalId])

  useEffect(() => {
    void (async () => {
      setIsLoading(true)
      await load()
      setIsLoading(false)
    })()
  }, [load])

  const handleResolved = useCallback(
    (suggestion: AdvisorySuggestion, action: 'accepted' | 'declined') => {
      setSuggestions((prev) => prev.filter((s) => s.id !== suggestion.id))
      setResolved((prev) => [{ suggestion, action }, ...prev])
    },
    [],
  )

  // While the first load is in flight there is no honest verdict to show, and a
  // spinner here would clutter the calm home. Stay quiet until we know.
  if (isLoading) return null

  // Honest failure — say plainly we couldn't check, never an empty all-quiet.
  if (error) {
    return (
      <div
        role="alert"
        className="mb-5 flex items-start gap-2 rounded-2xl border border-danger/30 bg-danger-light p-4 text-sm text-danger"
      >
        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
        <span>
          {error} Nothing is shown here because nothing could be loaded.
        </span>
      </div>
    )
  }

  // Empty list → render NOTHING (no "no suggestions" spam block) — unless the
  // client just resolved one this session, in which case we acknowledge it.
  if (suggestions.length === 0 && resolved.length === 0) return null

  return (
    <section aria-label="Suggestions from your team" className="mb-5">
      <AnimatePresence initial={false}>
        {suggestions.map((s) => (
          <SuggestionCard
            key={s.id}
            suggestion={s}
            onResolved={(action) => handleResolved(s, action)}
          />
        ))}
      </AnimatePresence>

      {/* Just-happened acknowledgements — real actions from this session only. */}
      {resolved.map(({ suggestion, action }) => (
        <div
          key={suggestion.id}
          className="rounded-2xl border border-gray-100 bg-white/60 p-3 mb-3"
        >
          <div className="flex items-start gap-2">
            <Check
              className="w-3.5 h-3.5 text-success mt-0.5 shrink-0"
              aria-hidden="true"
            />
            <p className="text-xs text-gray-600">
              {action === 'accepted'
                ? 'Thanks — your team has noted that.'
                : 'No problem — we won’t suggest that again for a while.'}
            </p>
          </div>
        </div>
      ))}
    </section>
  )
}
